import { XCircle, CheckCircle, AlertTriangle, Camera, Mail, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

const ReturnPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Strip */}
      <div className="bg-blue-700 text-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-blue-200 text-sm font-medium uppercase tracking-widest mb-2">Legal</p>
          <h1 className="text-4xl font-extrabold mb-3">Return &amp; Refund Policy</h1>
          <p className="text-blue-100">Last updated: April 2026</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12 space-y-6">
        {/* General Policy */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">General Policy</h2>
              <p className="text-sm text-gray-500">Our baseline position on returns</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            <strong>CollegeDrip does not accept returns or exchanges.</strong> All sales are final.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed mt-3">
            We're a small startup offering custom-printed, made-to-order merchandise. Because each item is
            produced specifically for your order, we're unable to resell returned products. This policy
            helps us keep prices fair and sustainable for everyone.
          </p>
        </div>

        {/* Exceptions — We WILL help */}
        <div className="bg-white rounded-xl border border-green-200 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">When We Will Make It Right</h2>
              <p className="text-sm text-gray-500">These exceptions are non-negotiable — we'll fix them</p>
            </div>
          </div>

          <p className="text-sm text-gray-700 mb-4">
            We will offer a <strong>full refund or free replacement</strong> in the following cases:
          </p>

          <div className="space-y-3">
            {[
              {
                title: "Wrong product received",
                desc: "You received a different item from what you ordered (wrong design, college, or product type).",
              },
              {
                title: "Manufacturing or printing defect",
                desc: "The product has a visible defect caused during production — misaligned print, colour bleed, stitching issues, etc.",
              },
              {
                title: "Damaged on delivery",
                desc: "The product arrived physically damaged. You must report this within 48 hours of delivery with photographic evidence.",
              },
            ].map(({ title, desc }) => (
              <div key={title} className="flex items-start gap-3 bg-green-50 rounded-lg p-4">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{title}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How to Raise */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <Camera className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">How to Report an Issue</h2>
              <p className="text-sm text-gray-500">Follow these steps within 48 hours of delivery</p>
            </div>
          </div>

          <ol className="space-y-4">
            {[
              {
                step: "1",
                title: "Take clear photos",
                desc: "Photograph the product clearly showing the defect, damage, or wrong item. Include the packaging if damaged.",
              },
              {
                step: "2",
                title: "Email us",
                desc: (
                  <>
                    Send an email to{" "}
                    <a
                      href="mailto:support@collegedrip.in"
                      className="text-blue-600 font-medium hover:underline"
                    >
                      support@collegedrip.in
                    </a>{" "}
                    with your <strong>Order ID</strong>, the photos, and a short description of the issue.
                  </>
                ),
              },
              {
                step: "3",
                title: "We respond within 48 hours",
                desc: "Our team will review and confirm whether your case qualifies. We aim to respond faster.",
              },
              {
                step: "4",
                title: "Resolution",
                desc: "We'll either send a replacement or process a refund, depending on your preference and stock availability.",
              },
            ].map(({ step, title, desc }) => (
              <div key={step} className="flex gap-4">
                <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {step}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{title}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </ol>
        </div>

        {/* Refund Timeline */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <RefreshCw className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Refund Timeline</h2>
              <p className="text-sm text-gray-500">When approved</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            Approved refunds are credited back to the <strong>original payment method</strong> within{" "}
            <strong>5–7 business days</strong> via Razorpay. You will receive an email confirmation when
            the refund is initiated.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Bank processing time after initiation may add 1–3 additional days depending on your bank.
          </p>
        </div>

        {/* Not Eligible */}
        <div className="bg-white rounded-xl border border-red-100 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Not Eligible for Refund or Return</h2>
              <p className="text-sm text-gray-500">These cases will not be accepted</p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              "Size issues — please refer to the size chart on the product page before ordering.",
              "Change of mind after placing an order.",
              "Minor colour variation due to screen calibration differences (this is normal and not a defect).",
              "Damage caused by misuse, washing machine use on delicate items, or improper care.",
            ].map((item) => (
              <div key={item} className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600">{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom note */}
        <div className="bg-gray-100 rounded-xl p-5 text-center">
          <p className="text-sm text-gray-600">
            This policy will be reviewed and expanded as CollegeDrip grows.
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Questions?{" "}
            <Link to="/contact" className="text-blue-600 hover:underline font-medium">
              Contact us
            </Link>{" "}
            or email{" "}
            <a href="mailto:support@collegedrip.in" className="text-blue-600 hover:underline">
              support@collegedrip.in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicyPage;